import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RiFacebookFill, RiGithubFill, RiTwitterXFill } from "@remixicon/react";
import { useAuth } from '../../Contexts/AuthContext';
import { useNotification } from '../../Contexts/NotificationContext';

export function SignInPage({ onToggleView }) {
  const navigate = useNavigate();
  const { signIn, signInWithProvider } = useAuth();
  const { addNotification } = useNotification();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await signIn(formData.email, formData.password);
      
      if (result.success) {
        addNotification('Welcome back! You are signed in.', 'success');

        // Redirect based on role
        switch (result.role) {
          case 'admin':
            navigate('/admin');
            break;
          case 'vendor':
            navigate('/vendor-portal');
            break;
          case 'customer':
            navigate('/customer-portal');
            break;
          case 'vendor_pending':
            addNotification('Your vendor application is pending approval.', 'info');
            navigate('/');
            break;
          default:
            navigate('/');
        }
      } else {
        addNotification(result.error || 'Sign in failed. Please check your credentials.', 'error');
      }
    } catch (error) {
      addNotification(error.message || 'Sign in failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    // Show warning message for now
    addNotification('Social Login Coming Soon - Please use email login for now!', 'warning', 5000);
    return;
    
    // TODO: Uncomment when social login is configured
    /*
    setLoading(true);
    try {
      const result = await signInWithProvider(provider);
      if (!result.success) {
        addNotification(result.error || 'Social login failed.', 'error');
      }
      // Success will be handled by the auth state change listener
    } catch (error) {
      addNotification(error.message || 'Social login failed.', 'error');
    } finally {
      setLoading(false);
    }
    */
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Sign in to TRND Marketplace</h1>
          <p>Welcome back! Please sign in to continue</p>
        </div>
        
        {/* Social login hidden until OAuth providers are configured */}
        {false && (<>
        <div className="social-auth-buttons">
          <button
            className="social-button google"
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
            type="button"
          >
            <span className="social-icon">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            </span>
            Login with Google
          </button>
          
          <button 
            className="social-button twitter"
            onClick={() => handleSocialLogin('twitter')}
            disabled={loading}
            type="button"
          >
            <RiTwitterXFill className="social-icon" size={18} />
            Login with X
          </button>
          
          <button 
            className="social-button facebook"
            onClick={() => handleSocialLogin('facebook')}
            disabled={loading}
            type="button"
          >
            <RiFacebookFill className="social-icon" size={18} />
            Login with Facebook
          </button>
          
          <button 
            className="social-button github"
            onClick={() => handleSocialLogin('github')}
            disabled={loading}
            type="button"
          >
            <RiGithubFill className="social-icon" size={18} />
            Login with GitHub
          </button>
        </div>
        
        <div className="auth-divider">
          <span>or</span>
        </div>
        </>)}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group" style={{ textAlign: 'right', marginTop: '-8px' }}>
            <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
          </div>

          <button type="submit" className="auth-submit-button" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>Don't have an account? <button type="button" onClick={(e) => { e.preventDefault(); onToggleView && onToggleView(); }} className="auth-link">Sign up</button></p>
        </div>
      </div>
    </div>
  );
} 